import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { fromNullable, Maybe } from 'maybeasy';
import { T } from '../../../../../../../Translations';
import { learningCollectionsStore } from '../../../../../../../stores/LearningCollectionsStore';
import { LearningCollectionProvider, useLearningCollection } from '../../LearningCollectionContext';
import ReadyBuilder from '../../ReadyBuilder';
import {
  EditLearningCollectionResource,
  ExperienceResourceArray,
  LearningCollectionResourceWithScheduledPrograms,
  ScheduledProgram,
} from '../../Types';
import { useBuilder } from './useBuilder';
import EditSkeleton from '../EditSkeleton';
import { useFilterOptions } from '../../Filters/useFilterOptions';
import { persistentFilterStore } from '../../Filters/PersistentFilterStore';

interface Props {
  id: string;
}

// Get all scheduled date IDs for an experience (returns empty array if not a scheduled experience)
function getAllScheduledDateIds(
  experienceId: number,
  allExperiences: ExperienceResourceArray,
): number[] {
  const experience = allExperiences.find((exp) => exp.payload.id === experienceId);
  if (!experience) return [];
  const scheduledDates = experience.payload.program_instance_options.scheduled;
  return scheduledDates.map((date) => date.id);
}

// Ensure all experiences in collection have a scheduled_programs entry with dates selected
// Backend only returns entries for experiences with saved records
// For scheduled experiences without saved entries OR with empty program_ids, default to ALL dates selected
function initializeScheduledPrograms(
  resource: EditLearningCollectionResource,
  catalog: Maybe<ExperienceResourceArray>,
): LearningCollectionResourceWithScheduledPrograms {
  const existingScheduledPrograms = fromNullable(
    (resource.payload as LearningCollectionResourceWithScheduledPrograms['payload'])
      .scheduled_programs,
  ).getOrElseValue([]);

  // Get all experience IDs from all product collections
  const allExperienceIds = resource.payload.product_collections.flatMap(
    (pc) => pc.payload.experiences,
  );

  // Create a map of existing entries for quick lookup
  const existingEntriesMap = new Map(existingScheduledPrograms.map((sp) => [sp.id, sp]));

  // Build scheduled_programs for all experiences in collection
  // - If entry exists with program_ids, keep it as-is
  // - If entry exists with empty program_ids, default to all dates (for scheduled experiences)
  // - If no entry exists, create one with all dates (for scheduled experiences)
  const scheduledPrograms: ScheduledProgram[] = allExperienceIds.map((id) => {
    const existingEntry = fromNullable(existingEntriesMap.get(id));

    // Until the catalog has loaded nothing tells a live run from an ended one,
    // so the stored selection stands as saved. Pruning against the empty
    // catalog cleared every selection, and the next save deleted the runs.
    return catalog
      .map((allExperiences) => {
        const allDates = getAllScheduledDateIds(id, allExperiences);

        // DVLP-7865: keep the stored selection minus runs that have ended. A
        // selection with nothing left falls through to the default below, so
        // the next save replaces the expired ids with upcoming runs.
        const liveSelection = existingEntry
          .map((entry) => entry.program_ids.filter((programId) => allDates.includes(programId)))
          .getOrElseValue([]);
        if (liveSelection.length > 0) {
          return { id, program_ids: liveSelection };
        }

        // Default to all dates for scheduled experiences (new or empty entries)
        return {
          id,
          program_ids: allDates,
        };
      })
      .getOrElse(() => existingEntry.getOrElseValue({ id, program_ids: [] }));
  });

  return {
    ...resource,
    payload: {
      ...resource.payload,
      scheduled_programs: scheduledPrograms,
    },
  };
}

// Inner component to use the context. An observer so it re-renders when the
// catalog store lands after the collection, which is the usual order on a
// fresh page load.
const EditBuilderContent = observer(function EditBuilderContent({
  resource,
}: {
  resource: EditLearningCollectionResource;
}) {
  const { resourceState, setResourceState } = useLearningCollection();

  // Initiate filter options query - will be cached for 15 minutes
  useFilterOptions(resource.links);

  // Get all experiences from the store to look up scheduled dates
  const storeExperiences =
    learningCollectionsStore.state.kind === 'ready'
      ? learningCollectionsStore.state.data.resource.payload.experiences
      : null;

  const catalog = useMemo(() => fromNullable(storeExperiences), [storeExperiences]);

  // Initialize scheduled_programs with entries for all experiences
  // Default to all dates selected for scheduled experiences without saved entries
  const initializedResource = useMemo(
    () => initializeScheduledPrograms(resource, catalog),
    [resource, catalog],
  );

  useEffect(() => {
    setResourceState(initializedResource);
  }, [initializedResource, setResourceState]);

  // The fetched resource only reaches the context after the effect above
  // runs; rendering ReadyBuilder before then paints a frame of the empty
  // initial context (blank builder). Hold the skeleton until the context
  // carries the loaded collection.
  return resourceState.payload.id.isNothing() ? (
    <EditSkeleton />
  ) : (
    <ReadyBuilder stateKind="edit" />
  );
});

function Builder({ id }: Props) {
  const { state } = useBuilder(id);

  // Clear persistent filter store when the Builder unmounts (leaving the editor)
  useEffect(() => {
    return () => {
      persistentFilterStore.clear();
    };
  }, []);

  switch (state.kind) {
    case 'loading':
      return <EditSkeleton />;
    case 'errored':
      return <T kind="oops learning collection failed!" />;
    case 'ready':
      return (
        <LearningCollectionProvider>
          <EditBuilderContent resource={state.data.resource} />
        </LearningCollectionProvider>
      );
  }
}

export default observer(Builder);
